const CEP_STORAGE_KEY = 'carraro:cep';
const CEP_LOCATION_STORAGE_KEY = 'carraro:cep-location';

/**
 * Returns at most eight numeric CEP characters.
 * @param {string} value
 */
function normalizeCep(value) {
  return value.replace(/\D/g, '').slice(0, 8);
}

/**
 * Formats a normalized CEP for display.
 * @param {string} value
 */
function formatCep(value) {
  const cep = normalizeCep(value);
  return cep.length > 5 ? `${cep.slice(0, 5)}-${cep.slice(5)}` : cep;
}

/**
 * Looks up a CEP's city/state via ViaCEP.
 * @param {string} cep - 8-digit CEP, digits only.
 */
async function lookupCepLocation(cep) {
  // Same per-CEP cache the PDP and cart widgets use: a CEP's city never changes.
  const cacheKey = 'carraro:viacep:' + cep;
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) return JSON.parse(cached);
  } catch (_) {
    // sessionStorage can be unavailable in restricted browser contexts.
  }

  try {
    const res = await fetch('https://viacep.com.br/ws/' + cep + '/json/');
    const data = await res.json();
    if (data.erro) return null;
    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(data));
    } catch (_) {
      // Caching is best-effort.
    }
    return data;
  } catch (_) {
    return null;
  }
}

/**
 * Updates every "Cidade - UF" display on the page, in sync with formatCep's
 * own document-wide broadcast for the CEP input itself.
 * @param {string} text
 * @param {{isError?: boolean}} [options]
 */
function displayCepLocation(text, { isError = false } = {}) {
  document.querySelectorAll('[data-cep-location]').forEach((el) => {
    el.textContent = text;
    el.hidden = !text;
    el.classList.toggle('is-error', isError);
  });
}

/**
 * Flags (or clears) every CEP input on the page as invalid, in sync with the
 * same document-wide broadcast the input value and location text use.
 * @param {boolean} isInvalid
 */
function setCepInvalid(isInvalid) {
  document.querySelectorAll('[data-cep-input]').forEach((input) => {
    input.classList.toggle('is-invalid', isInvalid);
    if (isInvalid) input.setAttribute('aria-invalid', 'true');
    else input.removeAttribute('aria-invalid');
  });
}

/**
 * Connects every CEP form in a given header area.
 * @param {ParentNode} scope
 */
function initializeCepFields(scope) {
  scope.querySelectorAll('[data-cep-form]').forEach((cepForm) => {
    if (!(cepForm instanceof HTMLFormElement) || cepForm.dataset.initialized === 'true') return;

    const cepInput = cepForm.querySelector('[data-cep-input]');
    if (!(cepInput instanceof HTMLInputElement)) return;
    cepForm.dataset.initialized = 'true';

    /** CEP the shopper gave in an earlier tab, kept on the cart; only used when this tab has none. */
    let cartCep = normalizeCep(cepForm.dataset.savedCep || '');
    /** A CEP stored by another widget (e.g. the PDP) before this ran, still without its "Cidade - UF". */
    let cepWithoutLocation = '';

    try {
      const storedCep = normalizeCep(sessionStorage.getItem(CEP_STORAGE_KEY) || '');
      if (storedCep) {
        cepInput.value = formatCep(storedCep);
        cartCep = '';
      }

      const storedLocation = sessionStorage.getItem(CEP_LOCATION_STORAGE_KEY) || '';
      if (storedLocation) displayCepLocation(storedLocation);
      else if (storedCep.length === 8) cepWithoutLocation = storedCep;
    } catch (_) {
      // sessionStorage can be unavailable in restricted browser contexts.
    }

    // Looks up and displays the "Cidade - UF" line for a CEP that's already
    // been persisted/broadcast. Split out from saveCep() so the external
    // change listener below can reuse it without re-dispatching
    // carraro:cep-change — every header instance (desktop + mobile) listens
    // for that event, so re-dispatching from inside its own handler would
    // bounce back and forth between them forever.
    const updateLocationDisplay = (cep) => {
      lookupCepLocation(cep).then((address) => {
        if (address && address.localidade && address.uf) {
          const text = `${address.localidade} - ${address.uf}`;
          displayCepLocation(text);
          setCepInvalid(false);

          try {
            sessionStorage.setItem(CEP_LOCATION_STORAGE_KEY, text);
          } catch (_) {
            // sessionStorage can be unavailable in restricted browser contexts.
          }
        } else {
          displayCepLocation('CEP não encontrado. Verifique e tente novamente.', { isError: true });
          setCepInvalid(true);

          try {
            sessionStorage.removeItem(CEP_LOCATION_STORAGE_KEY);
          } catch (_) {
            // sessionStorage can be unavailable in restricted browser contexts.
          }
        }
      });
    };

    const saveCep = () => {
      const cep = normalizeCep(cepInput.value);
      cepInput.value = formatCep(cep);

      if (cep.length !== 8) {
        displayCepLocation('CEP incompleto.', { isError: true });
        setCepInvalid(true);
        return;
      }

      try {
        sessionStorage.setItem(CEP_STORAGE_KEY, cep);
      } catch (_) {
        return;
      }

      document.querySelectorAll('[data-cep-input]').forEach((input) => {
        if (input instanceof HTMLInputElement && input !== cepInput) input.value = formatCep(cep);
      });

      document.dispatchEvent(new CustomEvent('carraro:cep-change', { detail: { cep } }));

      updateLocationDisplay(cep);
    };

    cepInput.addEventListener('input', () => {
      cepInput.value = formatCep(cepInput.value);
      const length = normalizeCep(cepInput.value).length;

      if (length === 8) {
        saveCep();
      } else if (cepInput.classList.contains('is-invalid')) {
        // Clear the stale error as soon as the shopper starts correcting it,
        // instead of leaving it up until they hit 8 digits again.
        displayCepLocation('');
        setCepInvalid(false);
      }
    });

    cepForm.addEventListener('submit', (event) => {
      event.preventDefault();
      saveCep();
    });

    // A new tab starts with an empty sessionStorage, but the cart still has
    // the CEP: restore it and announce it like a fresh save, so the header,
    // cart and PDP all show (and quote) the same CEP. saveCep stores it first,
    // so the other header instance (desktop/mobile) finds it and skips this.
    if (cartCep.length === 8) {
      cepInput.value = formatCep(cartCep);
      saveCep();
    } else if (cepWithoutLocation) {
      updateLocationDisplay(cepWithoutLocation);
    }

    // The CEP can also be set from the cart drawer, the cart page, or the
    // PDP's own shipping estimator — all of which broadcast this event
    // after saving. Without this, the header's own field value updates
    // (each of those already syncs every [data-cep-input]) but its
    // "Cidade - UF" line never does, since only saveCep() above triggers
    // the lookup.
    document.addEventListener('carraro:cep-change', (event) => {
      const cep = normalizeCep(event.detail?.cep || '');
      if (cep.length !== 8) return;

      cepInput.value = formatCep(cep);
      updateLocationDisplay(cep);
    });
  });
}

/**
 * "Entregar em" trigger in the desktop menu row: opens a small popover with the
 * CEP form (the regular [data-cep-form], so saving/syncing stays in
 * initializeCepFields) and keeps the trigger's summary line in sync.
 * @param {ParentNode} scope
 */
function initializeCepPopover(scope) {
  scope.querySelectorAll('[data-cep-popover]').forEach((host) => {
    if (!(host instanceof HTMLElement) || host.dataset.initialized === 'true') return;

    const trigger = host.querySelector('[data-cep-trigger]');
    const panel = host.querySelector('[data-cep-panel]');
    const overlay = host.querySelector('[data-cep-overlay]');
    const input = host.querySelector('[data-cep-input]');
    const summary = host.querySelector('[data-cep-summary]');
    const location = host.querySelector('[data-cep-location]');
    const geolocate = host.querySelector('[data-cep-geolocate]');
    const geolocateLabel = host.querySelector('[data-cep-geolocate-label]');
    if (!(trigger instanceof HTMLElement) || !(panel instanceof HTMLElement) || !(input instanceof HTMLInputElement)) return;
    host.dataset.initialized = 'true';

    /** @type {number | undefined} */
    let autoCloseTimer;

    const refreshSummary = () => {
      if (!(summary instanceof HTMLElement)) return;
      let cep = '';
      let place = '';
      try {
        cep = normalizeCep(sessionStorage.getItem(CEP_STORAGE_KEY) || '');
        place = sessionStorage.getItem(CEP_LOCATION_STORAGE_KEY) || '';
      } catch (_) {
        // sessionStorage can be unavailable in restricted browser contexts.
      }
      if (cep.length === 8) {
        // Stored as "Cidade - UF"; the trigger shows just "Cidade / UF".
        summary.textContent = place ? place.replace(/\s+-\s+([A-Z]{2})$/, ' / $1') : formatCep(cep);
      } else {
        summary.textContent = 'Informe seu CEP';
      }
    };

    const isOpen = () => !panel.hidden;

    const open = () => {
      window.clearTimeout(autoCloseTimer);
      panel.hidden = false;
      if (overlay instanceof HTMLElement) overlay.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      window.setTimeout(() => {
        input.focus();
        input.select();
      }, 30);
    };

    const close = ({ restoreFocus = false } = {}) => {
      window.clearTimeout(autoCloseTimer);
      panel.hidden = true;
      if (overlay instanceof HTMLElement) overlay.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      if (restoreFocus) trigger.focus();
    };

    trigger.addEventListener('click', () => (isOpen() ? close() : open()));
    host.querySelector('[data-cep-close]')?.addEventListener('click', () => close({ restoreFocus: true }));
    overlay?.addEventListener('click', () => close());

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && isOpen()) close({ restoreFocus: true });
    });

    document.addEventListener('click', (event) => {
      if (isOpen() && event.target instanceof Node && !host.contains(event.target)) close();
    });

    // The shared CEP logic writes the "Cidade - UF" (or an error) into
    // [data-cep-location] once the lookup finishes: mirror it on the trigger
    // and close the popover shortly after a successful lookup.
    if (location instanceof HTMLElement) {
      new MutationObserver(() => {
        refreshSummary();
        const ok = !location.hidden && location.textContent.trim() && !location.classList.contains('is-error');
        if (ok && isOpen()) {
          window.clearTimeout(autoCloseTimer);
          autoCloseTimer = window.setTimeout(() => close(), 900);
        }
      }).observe(location, { childList: true, characterData: true, subtree: true, attributes: true });
    }

    document.addEventListener('carraro:cep-change', refreshSummary);

    if (geolocate instanceof HTMLElement) {
      const setGeoLabel = (text) => {
        if (geolocateLabel instanceof HTMLElement) geolocateLabel.textContent = text;
      };

      geolocate.addEventListener('click', () => {
        if (!('geolocation' in navigator)) {
          setGeoLabel('Localização indisponível neste navegador');
          return;
        }
        setGeoLabel('Buscando sua localização…');
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            try {
              const { latitude, longitude } = position.coords;
              const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=jsonv2&addressdetails=1&lat=${latitude}&lon=${longitude}`,
                { headers: { 'Accept-Language': 'pt-BR' } }
              );
              const data = await response.json();
              const cep = normalizeCep((data && data.address && data.address.postcode) || '');
              if (cep.length === 8) {
                input.value = formatCep(cep);
                input.dispatchEvent(new Event('input', { bubbles: true }));
                setGeoLabel('Usar minha localização');
              } else {
                setGeoLabel('Não encontramos o CEP, digite acima');
              }
            } catch (_) {
              setGeoLabel('Não foi possível obter o CEP, digite acima');
            }
          },
          () => setGeoLabel('Permita o acesso à localização ou digite o CEP'),
          { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 }
        );
      });
    }

    refreshSummary();
  });
}

const HEADER_SEARCH_STYLES = `
.header-search-suggest{position:absolute;top:calc(100% + 6px);left:0;right:0;z-index:60;background:#fff;border:1px solid #e3e8ec;border-radius:12px;box-shadow:0 12px 32px rgba(27,58,78,.14);max-height:min(70vh,560px);overflow-y:auto;padding:8px 0;text-align:left}
.header-search-suggest[hidden]{display:none}
.header-search-suggest__group{padding:4px 0}
.header-search-suggest__group+.header-search-suggest__group{border-top:1px solid #eef1f4}
.header-search-suggest__label{display:block;padding:6px 16px;font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#6b7c88}
.header-search-suggest__item{display:flex;align-items:center;gap:12px;padding:8px 16px;color:#1b3a4e;text-decoration:none;font-size:14px;line-height:1.3}
.header-search-suggest__item:hover,.header-search-suggest__item[aria-selected="true"]{background:#f1f9f9}
.header-search-suggest__item mark{background:none;color:inherit;font-weight:700}
.header-search-suggest__thumb{flex:0 0 48px;width:48px;height:48px;border-radius:6px;object-fit:contain;background:#f6f7f8}
.header-search-suggest__info{display:flex;flex-direction:column;gap:2px;min-width:0}
.header-search-suggest__title{overflow:hidden;text-overflow:ellipsis;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical}
.header-search-suggest__price{font-size:13px;font-weight:700;color:#00a4aa}
.header-search-suggest__price s{color:#8a98a3;font-weight:400;margin-right:6px}
.header-search-suggest__pix{font-size:11px;font-weight:700;color:#32bcad}
.header-search-suggest__all{display:block;padding:10px 16px 6px;font-size:14px;font-weight:700;color:#00a4aa;text-decoration:none}
.header-search-suggest__empty{padding:12px 16px;font-size:14px;color:#6b7c88}
.header-search-suggest__trend{flex:0 0 auto;color:#00a4aa}
.header-search-suggest__rank{flex:0 0 auto;display:inline-flex;align-items:center;justify-content:center;min-width:30px;height:24px;padding:0 6px;border-radius:12px;background:#e8f4f4;color:#007f84;font-size:12px;font-weight:700}
`;

/** Injects the suggestion panel styles once per page. */
function ensureHeaderSearchStyles() {
  if (document.getElementById('header-search-suggest-styles')) return;
  const style = document.createElement('style');
  style.id = 'header-search-suggest-styles';
  style.textContent = HEADER_SEARCH_STYLES;
  document.head.appendChild(style);
}

const brlFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

/** @param {unknown} value */
function escapeHtml(value) {
  const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  return String(value ?? '').replace(/[&<>"']/g, (c) => map[/** @type {keyof typeof map} */ (c)]);
}

/**
 * Escapes the text and wraps occurrences of the typed term in <mark>.
 * @param {string} text
 * @param {string} term
 */
function highlightTerm(text, term) {
  const safe = escapeHtml(text);
  const t = escapeHtml(term.trim());
  if (!t) return safe;
  const pattern = new RegExp(`(${t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'ig');
  return safe.replace(pattern, '<mark>$1</mark>');
}

/** @param {string|number} price */
function formatPrice(price) {
  const n = Number(price);
  return Number.isFinite(n) ? brlFormatter.format(n) : '';
}

/**
 * Preço no Pix de um produto das sugestões, com a mesma conta dos cards: 7% arredondado
 * e, na oferta relâmpago, o 1% sobre o Pix. Lê window.CarraroPricing (layout/theme.liquid).
 * @param {{id?: number|string, price?: string|number}} product
 */
function pixPriceHtml(product) {
  const pricing = window.CarraroPricing || {};
  const pix = Number(pricing.pix) || 0;
  let cents = Math.round(Number(product.price) * 100);
  if (!Number.isFinite(cents) || pix <= 0) return formatPrice(product.price);
  cents = Math.round((cents * (100 - pix)) / 100);
  const flash = pricing.flash || {};
  const flashIds = Array.isArray(flash.productIds) ? flash.productIds : [];
  if (Number(flash.discount) > 0 && flashIds.includes(Number(product.id)) && (!flash.endsAt || Date.now() < flash.endsAt)) {
    cents -= Math.floor((cents * Number(flash.discount)) / 100);
  }
  return `${formatPrice(cents / 100)} <small class="header-search-suggest__pix">no Pix</small>`;
}

/**
 * Inline predictive search for the Carraro header: the shopper keeps typing in
 * the header field itself and suggestions (search terms, collections and
 * products from /search/suggest.json) open in a dropdown right below it.
 * Enter submits to the regular search results page.
 * @param {ParentNode} scope
 */
function initializeHeaderSearch(scope) {
  scope.querySelectorAll('[data-header-search-form]').forEach((form) => {
    if (!(form instanceof HTMLFormElement) || form.dataset.initialized === 'true') return;

    const searchInput = form.querySelector('[data-header-search-input]');
    if (!(searchInput instanceof HTMLInputElement)) return;
    form.dataset.initialized = 'true';
    ensureHeaderSearchStyles();

    const shopify = /** @type {any} */ (window).Shopify;
    const root = (shopify && shopify.routes && shopify.routes.root) || '/';
    const panelId = `${searchInput.id || 'HeaderSearch'}-suggestions`;
    const panel = document.createElement('div');
    panel.className = 'header-search-suggest';
    panel.id = panelId;
    panel.setAttribute('role', 'listbox');
    panel.hidden = true;
    if (getComputedStyle(form).position === 'static') form.style.position = 'relative';
    form.appendChild(panel);

    searchInput.setAttribute('role', 'combobox');
    searchInput.setAttribute('aria-autocomplete', 'list');
    searchInput.setAttribute('aria-controls', panelId);
    searchInput.setAttribute('aria-expanded', 'false');

    /** @type {AbortController | null} */
    let controller = null;
    /** @type {number | undefined} */
    let debounceTimer;
    let activeIndex = -1;

    const close = () => {
      panel.hidden = true;
      searchInput.setAttribute('aria-expanded', 'false');
      activeIndex = -1;
    };

    const open = () => {
      panel.hidden = false;
      searchInput.setAttribute('aria-expanded', 'true');
    };

    const items = () => Array.from(panel.querySelectorAll('[data-suggest-item]'));

    /** @param {number} index */
    const setActive = (index) => {
      const list = items();
      list.forEach((el, i) => el.setAttribute('aria-selected', String(i === index)));
      activeIndex = index;
      if (list[index]) list[index].scrollIntoView({ block: 'nearest' });
    };

    /** @param {string} term */
    const searchUrl = (term) => `${root}search?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last&type=product`;

    /**
     * @param {any} resources
     * @param {string} term
     */
    const render = (resources, term) => {
      const queries = (resources.queries || []).slice(0, 4);
      const collections = (resources.collections || []).slice(0, 3);
      const products = (resources.products || []).slice(0, 6);
      let html = '';

      if (queries.length) {
        html += '<div class="header-search-suggest__group"><span class="header-search-suggest__label">Sugestões</span>';
        html += queries
          .map(
            (/** @type {any} */ q) =>
              `<a class="header-search-suggest__item" data-suggest-item role="option" href="${escapeHtml(q.url || searchUrl(q.text))}">${highlightTerm(q.text, term)}</a>`
          )
          .join('');
        html += '</div>';
      }

      if (collections.length) {
        html += '<div class="header-search-suggest__group"><span class="header-search-suggest__label">Categorias</span>';
        html += collections
          .map(
            (/** @type {any} */ c) =>
              `<a class="header-search-suggest__item" data-suggest-item role="option" href="${escapeHtml(c.url)}">${highlightTerm(c.title, term)}</a>`
          )
          .join('');
        html += '</div>';
      }

      if (products.length) {
        html += '<div class="header-search-suggest__group"><span class="header-search-suggest__label">Produtos</span>';
        html += products
          .map((/** @type {any} */ p) => {
            const image = p.featured_image && p.featured_image.url ? p.featured_image.url : p.image;
            const img = image
              ? `<img class="header-search-suggest__thumb" src="${escapeHtml(image)}${String(image).includes('?') ? '&' : '?'}width=120" alt="" width="48" height="48" loading="lazy">`
              : '<span class="header-search-suggest__thumb"></span>';
            const compare =
              Number(p.compare_at_price_max) > Number(p.price) ? `<s>${formatPrice(p.compare_at_price_max)}</s>` : '';
            return `<a class="header-search-suggest__item" data-suggest-item role="option" href="${escapeHtml(p.url)}">${img}<span class="header-search-suggest__info"><span class="header-search-suggest__title">${highlightTerm(p.title, term)}</span><span class="header-search-suggest__price">${compare}${pixPriceHtml(p)}</span></span></a>`;
          })
          .join('');
        html += '</div>';
      }

      if (!html) {
        html = `<div class="header-search-suggest__empty">Nenhum resultado para “${escapeHtml(term)}”.</div>`;
      } else {
        html += `<a class="header-search-suggest__all" data-suggest-item role="option" href="${escapeHtml(searchUrl(term))}">Ver todos os resultados para “${escapeHtml(term)}”</a>`;
      }

      panel.innerHTML = html;
      activeIndex = -1;
      open();
    };

    const popularTerms = (form.dataset.popularSearches || '')
      .split('|')
      .map((term) => term.trim())
      .filter(Boolean)
      .slice(0, 3);

    const trendIcon =
      '<svg class="header-search-suggest__trend" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M1.5 11.5l4.5-4.5 3 3 5.5-5.5M10 4.5h4.5V9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    const showPopular = () => {
      if (!popularTerms.length) return;
      if (controller) controller.abort();
      panel.innerHTML =
        '<div class="header-search-suggest__group"><span class="header-search-suggest__label">Mais buscados</span>' +
        popularTerms
          .map(
            (term, index) =>
              `<a class="header-search-suggest__item" data-suggest-item role="option" href="${escapeHtml(searchUrl(term))}">${trendIcon}<span class="header-search-suggest__rank">${index + 1}º</span>${escapeHtml(term)}</a>`
          )
          .join('') +
        '</div>';
      activeIndex = -1;
      open();
    };

    /** @param {string} term */
    const fetchSuggestions = async (term) => {
      if (controller) controller.abort();
      controller = new AbortController();
      const params = new URLSearchParams({
        q: term,
        'resources[type]': 'query,collection,product',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,variants.sku,vendor,tag',
      });
      try {
        const response = await fetch(`${root}search/suggest.json?${params}`, { signal: controller.signal });
        if (!response.ok) return;
        const data = await response.json();
        if (searchInput.value.trim() !== term) return;
        render((data && data.resources && data.resources.results) || {}, term);
      } catch (error) {
        if (!(error instanceof DOMException && error.name === 'AbortError')) close();
      }
    };

    searchInput.addEventListener('input', () => {
      const term = searchInput.value.trim();
      window.clearTimeout(debounceTimer);
      if (term.length < 2) {
        if (controller) controller.abort();
        if (term.length === 0) showPopular();
        else close();
        return;
      }
      debounceTimer = window.setTimeout(() => fetchSuggestions(term), 200);
    });

    searchInput.addEventListener('focus', () => {
      const term = searchInput.value.trim();
      if (term.length === 0) showPopular();
      else if (term.length >= 2 && panel.innerHTML) open();
    });

    // Clicking an already-focused empty field (after Esc / outside click) reopens it.
    searchInput.addEventListener('click', () => {
      if (panel.hidden && searchInput.value.trim().length === 0) showPopular();
    });

    searchInput.addEventListener('keydown', (event) => {
      const list = items();
      if (event.key === 'Escape') {
        close();
      } else if (event.key === 'ArrowDown' && !panel.hidden && list.length) {
        event.preventDefault();
        setActive((activeIndex + 1) % list.length);
      } else if (event.key === 'ArrowUp' && !panel.hidden && list.length) {
        event.preventDefault();
        setActive(activeIndex <= 0 ? list.length - 1 : activeIndex - 1);
      } else if (event.key === 'Enter' && !panel.hidden && list[activeIndex] instanceof HTMLAnchorElement) {
        event.preventDefault();
        window.location.href = /** @type {HTMLAnchorElement} */ (list[activeIndex]).href;
      }
    });

    document.addEventListener('click', (event) => {
      if (event.target instanceof Node && !form.contains(event.target)) close();
    });

    form.addEventListener('submit', (event) => {
      if (!searchInput.value.trim()) event.preventDefault();
    });
  });
}

/**
 * Initializes one Carraro header instance.
 * @param {HTMLElement} header
 */
function initializeHeader(header) {
  if (header.dataset.initialized === 'true') return;
  header.dataset.initialized = 'true';

  const submenuModal = header.querySelector('[data-submenu-modal]');
  const submenuTriggers = Array.from(header.querySelectorAll('[data-submenu-trigger]'));
  const submenuPanels = Array.from(header.querySelectorAll('[data-submenu-panel]'));

  initializeCepFields(header);
  initializeCepPopover(header);
  initializeHeaderSearch(header);

  if (!(submenuModal instanceof HTMLElement)) return;

  const positionSubmenu = (trigger) => {
    if (!(trigger instanceof HTMLElement) || submenuModal.hidden) return;

    const headerRect = header.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
    const submenuWidth = submenuModal.offsetWidth;
    const viewportPadding = 8;
    const triggerCenter = triggerRect.left - headerRect.left + triggerRect.width / 2;
    const desiredLeft = triggerCenter - submenuWidth / 2;
    const maximumLeft = Math.max(viewportPadding, headerRect.width - submenuWidth - viewportPadding);
    const left = Math.min(Math.max(desiredLeft, viewportPadding), maximumLeft);

    submenuModal.style.setProperty('--submenu-left', `${left}px`);
    submenuModal.style.left = `${left}px`;
  };

  /** @type {ReturnType<typeof window.setTimeout> | null} */
  let closeTimer = null;

  const cancelScheduledClose = () => {
    if (closeTimer !== null) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }
  };

  // Stagger index for the cascading fade-in of each panel's items.
  submenuPanels.forEach((panel) => {
    panel.querySelectorAll('.submenu-item').forEach((item, index) => {
      if (item instanceof HTMLElement) item.style.setProperty('--submenu-item-index', String(index));
    });
  });

  /** @type {(() => void) | null} */
  let pendingHeightEnd = null;

  /**
   * Accordion-style height transition of the popup. `height: auto` can't be
   * transitioned, so it animates between measured pixel heights and clears
   * the inline height once it settles.
   * @param {number} from
   * @param {number} to
   * @param {() => void} [onDone]
   */
  const animateHeight = (from, to, onDone) => {
    if (pendingHeightEnd) submenuModal.removeEventListener('transitionend', pendingHeightEnd);
    pendingHeightEnd = null;

    if (from === to) {
      submenuModal.style.height = '';
      onDone?.();
      return;
    }

    submenuModal.style.height = `${from}px`;
    void submenuModal.offsetHeight;
    submenuModal.style.height = `${to}px`;

    const handleEnd = (/** @type {Event} */ event) => {
      if (event.target !== submenuModal || !(event instanceof TransitionEvent) || event.propertyName !== 'height') return;
      submenuModal.removeEventListener('transitionend', handleEnd);
      pendingHeightEnd = null;
      submenuModal.style.height = '';
      onDone?.();
    };
    pendingHeightEnd = handleEnd;
    submenuModal.addEventListener('transitionend', handleEnd);
  };

  /** Restarts the items' cascade on the next frame so the transition runs. */
  const revealItems = () => {
    submenuModal.classList.remove('is-open');
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => submenuModal.classList.add('is-open'));
    });
  };

  const closeSubmenu = ({ restoreFocus = false } = {}) => {
    cancelScheduledClose();

    const activeTrigger = submenuTriggers.find((trigger) => trigger.getAttribute('aria-expanded') === 'true');

    submenuTriggers.forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));

    if (!submenuModal.hidden && !submenuModal.classList.contains('is-closing')) {
      submenuModal.classList.add('is-closing');
      submenuModal.classList.remove('is-open');
      animateHeight(submenuModal.offsetHeight, 0, () => {
        submenuModal.classList.remove('is-closing');
        submenuModal.hidden = true;
        submenuPanels.forEach((panel) => {
          panel.hidden = true;
        });
      });
    }

    if (restoreFocus && activeTrigger instanceof HTMLElement) activeTrigger.focus();
  };

  // Leaving the trigger or the popup doesn't close it immediately — the
  // pointer needs a moment to travel the gap between them (they're
  // siblings, not nested), so the close is delayed and cancelled if the
  // pointer lands on the other one in time.
  const scheduleClose = () => {
    cancelScheduledClose();
    closeTimer = window.setTimeout(() => {
      closeTimer = null;
      closeSubmenu();
    }, 150);
  };

  const openSubmenu = (trigger) => {
    if (!(trigger instanceof HTMLElement)) return;

    cancelScheduledClose();
    if (trigger.getAttribute('aria-expanded') === 'true' && !submenuModal.classList.contains('is-closing')) return;

    const targetId = trigger.getAttribute('aria-controls');
    const targetPanel = targetId ? document.getElementById(targetId) : null;
    if (!(targetPanel instanceof HTMLElement)) return;

    const wasVisible = !submenuModal.hidden;
    const fromHeight = wasVisible ? submenuModal.offsetHeight : 0;

    submenuModal.classList.remove('is-closing');
    submenuTriggers.forEach((other) => other.setAttribute('aria-expanded', String(other === trigger)));
    submenuPanels.forEach((panel) => {
      panel.hidden = panel !== targetPanel;
    });
    submenuModal.hidden = false;
    positionSubmenu(trigger);

    // Measure the natural height of the new panel before animating to it.
    submenuModal.style.height = 'auto';
    const toHeight = submenuModal.offsetHeight;

    revealItems();
    animateHeight(fromHeight, toHeight);
  };

  submenuTriggers.forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      if (isOpen) {
        closeSubmenu();
      } else {
        openSubmenu(trigger);
      }
    });
    trigger.addEventListener('pointerenter', () => openSubmenu(trigger));
    trigger.addEventListener('pointerleave', scheduleClose);
    trigger.addEventListener('focus', () => openSubmenu(trigger));
  });

  submenuModal.addEventListener('pointerenter', cancelScheduledClose);
  submenuModal.addEventListener('pointerleave', scheduleClose);

  window.addEventListener('resize', () => {
    const activeTrigger = submenuTriggers.find((trigger) => trigger.getAttribute('aria-expanded') === 'true');
    if (activeTrigger instanceof HTMLElement) positionSubmenu(activeTrigger);
  });

  document.addEventListener('click', (event) => {
    if (event.target instanceof Node && !header.contains(event.target)) closeSubmenu();
  });

  header.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !submenuModal.hidden) {
      event.preventDefault();
      closeSubmenu({ restoreFocus: true });
    }
  });
}

/**
 * Initializes the mobile header, its off-canvas menu and account sheet.
 * @param {HTMLElement} mobileHeader
 */
function initializeMobileHeader(mobileHeader) {
  if (mobileHeader.dataset.initialized === 'true') return;
  mobileHeader.dataset.initialized = 'true';

  const sectionRoot = mobileHeader.closest('.shopify-section') || mobileHeader.parentElement;
  if (!(sectionRoot instanceof HTMLElement)) return;

  const menu = sectionRoot.querySelector('[data-mobile-menu]');
  const overlay = sectionRoot.querySelector('[data-mobile-overlay]');
  const accountPopup = sectionRoot.querySelector('[data-account-popup]');
  const menuOpen = mobileHeader.querySelector('[data-mobile-menu-open]');
  const menuClose = sectionRoot.querySelector('[data-mobile-menu-close]');
  const menuBack = sectionRoot.querySelector('[data-mobile-menu-back]');
  const accountOpen = sectionRoot.querySelector('[data-account-open]');
  const accountClose = sectionRoot.querySelector('[data-account-close]');
  const menuTrack = sectionRoot.querySelector('[data-mobile-menu-track]');
  const rootPage = sectionRoot.querySelector('[data-mobile-menu-page="root"]');
  const submenuPages = Array.from(sectionRoot.querySelectorAll('[data-mobile-menu-page="submenu"]'));
  const submenuOpeners = Array.from(sectionRoot.querySelectorAll('[data-mobile-submenu-open]'));

  initializeCepFields(mobileHeader);
  initializeCepPopover(mobileHeader);
  initializeHeaderSearch(mobileHeader);

  if (
    !(menu instanceof HTMLElement) ||
    !(overlay instanceof HTMLButtonElement) ||
    !(accountPopup instanceof HTMLElement) ||
    !(menuOpen instanceof HTMLElement) ||
    !(menuTrack instanceof HTMLElement)
  ) {
    return;
  }

  let lastFocusedElement = null;
  let pageResetTimer = null;
  let menuCloseTimer = null;

  const updatePage = (submenu = null) => {
    const hasSubmenu = submenu instanceof HTMLElement;
    rootPage?.setAttribute('aria-hidden', hasSubmenu ? 'true' : 'false');

    submenuPages.forEach((page) => {
      const isActive = page === submenu;
      page.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      if (hasSubmenu) page.classList.toggle('is-active', isActive);
    });

    if (menuBack instanceof HTMLElement) menuBack.hidden = !hasSubmenu;
    if (accountOpen instanceof HTMLElement) accountOpen.hidden = hasSubmenu;

    if (pageResetTimer !== null) {
      window.clearTimeout(pageResetTimer);
      pageResetTimer = null;
    }

    if (hasSubmenu) {
      // The submenu must be laid out before moving the track, otherwise it can
      // briefly render outside the drawer or skip the transition.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => menuTrack.classList.add('is-submenu'));
      });
    } else {
      menuTrack.classList.remove('is-submenu');
      pageResetTimer = window.setTimeout(() => {
        if (!menuTrack.classList.contains('is-submenu')) {
          submenuPages.forEach((page) => page.classList.remove('is-active'));
        }
        pageResetTimer = null;
      }, 260);
    }
  };

  const syncModalState = () => {
    const hasOpenModal = menu.classList.contains('is-open') || accountPopup.classList.contains('is-open');
    overlay.hidden = !hasOpenModal;
    document.body.classList.toggle('carraro-mobile-modal-open', hasOpenModal);
  };

  const closeMenu = ({ restoreFocus = true } = {}) => {
    menu.classList.remove('is-open');
    menu.setAttribute('aria-hidden', 'true');
    menuOpen.setAttribute('aria-expanded', 'false');

    if (menuCloseTimer !== null) window.clearTimeout(menuCloseTimer);
    // Keep the current page and therefore the drawer height stable while it
    // exits. Resetting a dynamic-height centered drawer during the transition
    // makes its vertical position jump.
    menuCloseTimer = window.setTimeout(() => {
      if (!menu.classList.contains('is-open')) updatePage();
      menuCloseTimer = null;
    }, 260);

    syncModalState();
    if (restoreFocus && lastFocusedElement instanceof HTMLElement) lastFocusedElement.focus();
  };

  const closeAccount = ({ restoreFocus = true } = {}) => {
    accountPopup.classList.remove('is-open');
    accountPopup.setAttribute('aria-hidden', 'true');
    menu.classList.remove('has-account-popup');
    syncModalState();
    if (restoreFocus && menu.classList.contains('is-open') && accountOpen instanceof HTMLElement) {
      accountOpen.focus({ preventScroll: true });
    } else if (restoreFocus && lastFocusedElement instanceof HTMLElement) {
      lastFocusedElement.focus();
    }
  };

  const closeAll = ({ restoreFocus = true } = {}) => {
    closeMenu({ restoreFocus: false });
    closeAccount({ restoreFocus: false });
    if (restoreFocus && lastFocusedElement instanceof HTMLElement) lastFocusedElement.focus();
  };

  menuOpen.addEventListener('click', () => {
    lastFocusedElement = menuOpen;
    if (menuCloseTimer !== null) {
      window.clearTimeout(menuCloseTimer);
      menuCloseTimer = null;
    }
    updatePage();
    menu.classList.add('is-open');
    menu.setAttribute('aria-hidden', 'false');
    menuOpen.setAttribute('aria-expanded', 'true');
    syncModalState();
    menu.querySelector('button, a')?.focus();
  });

  menuClose?.addEventListener('click', () => closeMenu());
  menuBack?.addEventListener('click', () => updatePage());

  submenuOpeners.forEach((opener) => {
    opener.addEventListener('click', () => {
      const targetId = opener.getAttribute('data-mobile-submenu-open');
      const targetPage = targetId ? document.getElementById(targetId) : null;
      if (targetPage instanceof HTMLElement) {
        updatePage(targetPage);
        window.setTimeout(() => {
          targetPage.querySelector('a, button')?.focus({ preventScroll: true });
        }, 250);
      }
    });
  });

  accountOpen?.addEventListener('click', () => {
    lastFocusedElement = menuOpen;
    menu.classList.add('has-account-popup');
    accountPopup.classList.add('is-open');
    accountPopup.setAttribute('aria-hidden', 'false');
    syncModalState();
    accountPopup.querySelector('a, button')?.focus({ preventScroll: true });
  });

  accountClose?.addEventListener('click', () => closeAccount());
  overlay.addEventListener('click', () => {
    if (accountPopup.classList.contains('is-open')) closeAccount();
    else closeMenu();
  });

  menu.addEventListener('click', (event) => {
    if (accountPopup.classList.contains('is-open') && event.target === menu) closeAccount();
  });

  sectionRoot.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !overlay.hidden) {
      event.preventDefault();
      if (accountPopup.classList.contains('is-open')) closeAccount();
      else closeMenu();
    }
  });
}

function initializeHeaders(scope = document) {
  scope.querySelectorAll('[data-carraro-header]').forEach((header) => {
    if (header instanceof HTMLElement) initializeHeader(header);
  });

  scope.querySelectorAll('[data-carraro-mobile]').forEach((mobileHeader) => {
    if (mobileHeader instanceof HTMLElement) initializeMobileHeader(mobileHeader);
  });
}

function initializeStickyAnnouncement() {
  const headerGroup = document.getElementById('header-group');
  const announcementSection = headerGroup?.querySelector('.header-announcements-section');
  const pageWrapper = document.querySelector('.page-wrapper');

  if (
    !(headerGroup instanceof HTMLElement) ||
    !(announcementSection instanceof HTMLElement) ||
    headerGroup.dataset.scrollAnnouncementInitialized === 'true'
  ) {
    return;
  }

  headerGroup.dataset.scrollAnnouncementInitialized = 'true';
  let lastScrollPosition = 0;
  let accumulatedScroll = 0;
  let lastDirection = 0;
  let transitionLockedUntil = 0;
  let ticking = false;

  const directionThreshold = 12;
  const transitionDuration = 280;

  const updateAnnouncementHeight = () => {
    headerGroup.style.setProperty('--announcement-bar-height', `${announcementSection.scrollHeight}px`);
  };

  const getScrollPosition = () => {
    if (window.matchMedia('(min-width: 990px)').matches && pageWrapper instanceof HTMLElement) {
      return pageWrapper.scrollTop;
    }
    return window.scrollY;
  };

  const updateVisibility = () => {
    const currentScrollPosition = Math.max(0, getScrollPosition());
    const delta = currentScrollPosition - lastScrollPosition;
    const now = performance.now();

    // Collapsing the announcement changes the header height and can create a
    // synthetic scroll in the opposite direction. Ignore it until the CSS
    // transition has settled so the header cannot alternate between states.
    if (now < transitionLockedUntil) {
      lastScrollPosition = currentScrollPosition;
      ticking = false;
      return;
    }

    if (currentScrollPosition <= 8) {
      if (headerGroup.classList.contains('is-announcement-hidden')) {
        headerGroup.classList.remove('is-announcement-hidden');
        transitionLockedUntil = now + transitionDuration;
      }
      accumulatedScroll = 0;
      lastDirection = 0;
    } else if (Math.abs(delta) > 0.5) {
      const direction = Math.sign(delta);

      if (direction !== lastDirection) accumulatedScroll = 0;
      accumulatedScroll += delta;
      lastDirection = direction;

      if (accumulatedScroll >= directionThreshold && !headerGroup.classList.contains('is-announcement-hidden')) {
        headerGroup.classList.add('is-announcement-hidden');
        transitionLockedUntil = now + transitionDuration;
        accumulatedScroll = 0;
      } else if (
        accumulatedScroll <= -directionThreshold &&
        headerGroup.classList.contains('is-announcement-hidden')
      ) {
        headerGroup.classList.remove('is-announcement-hidden');
        transitionLockedUntil = now + transitionDuration;
        accumulatedScroll = 0;
      }
    }

    lastScrollPosition = currentScrollPosition;
    ticking = false;
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(updateVisibility);
  };

  updateAnnouncementHeight();
  lastScrollPosition = getScrollPosition();
  window.addEventListener('scroll', onScroll, { passive: true });
  pageWrapper?.addEventListener('scroll', onScroll, { passive: true });

  const resizeObserver = new ResizeObserver(updateAnnouncementHeight);
  resizeObserver.observe(announcementSection);
}

initializeHeaders();
initializeStickyAnnouncement();

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) {
    initializeHeaders(event.target);
    initializeStickyAnnouncement();
  }
});
